import Debug from 'debug';
import { DfuError, ErrorCode } from '../pc-nrf-dfu-js/src/DfuError';
import DfuTransportHid from "./DfuTransportHid";
import * as slip from "../pc-nrf-dfu-js/src/util/slip";
import {report} from "process";

const debug = Debug('webhid');


/**
 * Generic USB HID transport. Supports ULX USB devices.
 *
 * This can be used to send and receive USB HID packets without any encoding/decoding.
 * It is instantiated with a webHid device instance, and an onData callback.
 */
export default class GenericTransportWebHid extends DfuTransportHid {
    // Creates the transport. A vid and pid (integer) is required,
    // and packetReceiptNotification may also be provided (defaults
    // to 16).
    constructor(webHidDevice, onData, packetReceiveNotification = 16) {
        super(null, packetReceiveNotification);

        this.dev = webHidDevice;
        this.mtu = 48; // NOTE: MUST be 4-byte aligned or flash writes will crash! // USB_HID max size = 64. 1 byte reserved for ReportID header
        this.onData = onData;
    }

    hidAvailable() {
        const available = "hid" in navigator;
        if (!available) {
            console.error('Sorry the WebHID API is not supported in your browser.<br>' +
                'Try enabling <span>Experimental Web Platform features</span> in <code>chrome://flags<code>.');
        }
        return available;
    }

    // Given a reportId and payload, sends it over HID, without SLIP encoding
    // This ensures that the serial port is open by calling this.open() - the first
    // call to writeRaw will actually open the port.
    writeReport(reportId, bytes) {
        // Strip the heading 0xC0 character, as to avoid a bug in the nRF SDK implementation
        // of the SLIP encoding/decoding protocol
        // encoded = encoded.subarray(1);

        // Cast the Uint8Array info a Buffer, so it works on nodejs v6
        let rawBytes = Buffer.from(bytes);

        // Replace first char with ReportID for HID transport
        // rawBytes[0] = NRF_REPORT_ID;

        return this.open().then(() => (
            new Promise(res => {
                const written = this.dev.sendReport(reportId, rawBytes);
                debug(' send --> ', rawBytes);
                if (written) {
                    res();
                } else {
                    rej();
                }
            })
        ));
    }

    // Override the DfuTransportHid implementation,
    // since we don't want SLIP decoding
    onRawData(reportId, data) {
        debug(' recv <-- ', data);
        // verify data came from nRF, by checking reportID
        if (this.onData) {
            this.onData(reportId, data);
        }
        // if (reportId == NRF_RESP_REPORT_ID) {
        //     return this.slipDecoder.decode(data);
        // } else {
        //     return;
        // }
    }

    // Opens the port that matches the serial number provided to the transport.
    // Returns a Promise when opening is done.
    open() {
        if (!this.dev) {
            return Promise.reject();
        }

        if (this.dev.opened) {
            return Promise.resolve();
        }

        console.log("Opening WebHID device: " + this.dev.vendorId + ":" + this.dev.productId + " " + this.dev.productName)
        return super.open();
    }

    // Waits for the target to disconnect. Times out if not
    // disconnected after 5000 ms.
    waitForDisconnect() {
        if (!this.dev || !this.dev.opened) {
            debug('dev is already closed.');
            return Promise.resolve();
        }

        // Explicit close in all cases
        return new Promise(resolve => {
            this.dev.close(() => {
                // resolve() must be delayed so macOS doesn't immediately
                // open the same device again which occurs when it is plugged in a hub,
                // and win7-x64 might also have the chance to clean up.
                setTimeout(resolve, 2000);
            });
        });
    }
}
